import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Heart, MessageSquare, Share2, MoreHorizontal, PlusCircle, ImageIcon, Send, Sparkles, TrendingUp, User as UserIcon, Search, X, Video, Loader } from 'lucide-react';
import { db } from './firebaseConfig';
import { User, Post } from './types';
import { getPostsFeed, createPost, likePost } from './services/postService';
import { uploadImage, uploadVideo, validateMediaFile } from './services/mediaService';
import { getPublicProfilesByIds, PublicProfile, searchPublicProfiles } from './services/publicProfileService';
import { followUser, unfollowUser, isFollowing, getFollowingList, getUserSuggestions, subscribeToFollowing } from './services/followService';
import { isValidAuth } from './utils/authGuard';

/**
 * Lightweight type for public user data used in UI
 * This is a subset of PublicProfile with only fields needed for display
 */
type PublicUserInfo = {
  id: string;
  userId: string;
  name: string;
  avatar: string;
  level: number;
  xp?: number;
  title?: string;
};

// Component to show followed users (real-time subscription from userConnections)
const FollowingUsersList = ({ user, authReady, setView }: { user: User, authReady: boolean, setView?: (view: string) => void }) => {
  const { t } = useTranslation();
  const [followedUsers, setFollowedUsers] = useState<PublicUserInfo[]>([]);

  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // Subscribe to real-time following list changes
    const unsubscribe = subscribeToFollowing(user.id, async (followingIds) => {
      try {
        if (followingIds.length === 0) {
          setFollowedUsers([]);
          return;
        }

        // Fetch public profiles for followed users
        const profiles = await getPublicProfilesByIds(followingIds);
        
        // Convert PublicProfile[] to PublicUserInfo[] for UI
        const users: PublicUserInfo[] = profiles.map(profile => ({
          id: profile.userId,
          userId: profile.userId,
          name: profile.name,
          avatar: profile.avatar,
          level: profile.level,
          xp: profile.xp,
          title: profile.title,
        }));
        
        setFollowedUsers(users);
        
        if (import.meta.env.DEV) {
          console.log('[FEED] Following list updated (real-time):', {
            userId: user.id,
            followingCount: followingIds.length,
            profilesLoaded: users.length
          });
        }
      } catch (error: any) {
        // CRITICAL: Handle errors gracefully - don't crash the app
        if (import.meta.env.DEV) {
          console.error('[FEED] Error loading following profiles:', {
            code: error?.code,
            message: error?.message
          });
        }
        // Set empty array on error - don't block rendering
        setFollowedUsers([]);
      }
    });

    return () => {
      try {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      } catch {
        // Silently ignore cleanup errors
      }
    };
  }, [authReady, user.id]);

  if (followedUsers.length === 0) {
    return null; // Don't show anything if no friends
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-2">
      {followedUsers.map(followedUser => (
        <div 
          key={followedUser.id}
          onClick={() => setView && setView(`profile:${followedUser.id}`)}
          className="flex-shrink-0 w-16 h-16 rounded-full border-2 border-slate-800 p-1 hover:border-emerald-500 transition-all cursor-pointer relative group"
        >
          <img 
            src={followedUser.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${followedUser.id}`} 
            alt={followedUser.name} 
            className="w-full h-full rounded-full object-cover" 
          />
          {/* Tooltip on hover */}
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-black/80 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
            {followedUser.name}
          </div>
        </div>
      ))}
    </div>
  );
};

export const SocialFeedView = ({ user, authReady, setView }: { user: User, authReady: boolean, setView?: (view: string) => void }) => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<Post[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newPostText, setNewPostText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [fileType, setFileType] = useState<'image' | 'video' | null>(null);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PublicUserInfo[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [followingStatus, setFollowingStatus] = useState<Record<string, boolean>>({});
  const [suggestions, setSuggestions] = useState<PublicUserInfo[]>([]);
  const [followingCount, setFollowingCount] = useState<number>(0); // Track following count for empty state intelligence
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // 🔒 CRITICAL FIX: Use one-shot getDocs() instead of onSnapshot for feed
  // Feed is public data - no realtime needed, and onSnapshot causes permission-denied errors
  useEffect(() => {
    // 🔑 CRITICAL: Block ALL operations until auth is ready
    if (!authReady) return;
    
    let mounted = true;

    const loadFeed = async () => {
      try {
        if (import.meta.env.DEV) {
          console.log('[FEED] Loading posts feed (one-shot)...');
        }
        
        const feedPosts = await getPostsFeed(20);
        
        if (!mounted) return;
        
        if (import.meta.env.DEV) {
          console.log('[FEED] Received posts:', feedPosts.length);
          
          // Log unique user IDs to verify multiple users are shown
          const uniqueUserIds = [...new Set(feedPosts.map(p => p.userId))];
          const uniqueUserNames = [...new Set(feedPosts.map(p => p.userName))];
          
          console.log('[FEED] Posts from', uniqueUserIds.length, 'unique users:', {
            userIds: uniqueUserIds,
            userNames: uniqueUserNames,
            currentUserId: user.id,
            postsFromCurrentUser: feedPosts.filter(p => p.userId === user.id).length,
            postsFromOtherUsers: feedPosts.filter(p => p.userId !== user.id).length
          });
        }
        
        setPosts(feedPosts || []);
      } catch (error: any) {
        if (import.meta.env.DEV) {
          console.warn('[FEED] Error loading feed (fallback to empty):', error);
        }
        if (mounted) {
          setPosts([]);
        }
      }
    };

    loadFeed();
    
    return () => {
      mounted = false;
    };
  }, [authReady, user.id]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Determine file type
    if (file.type.startsWith('image/')) {
      const validation = validateMediaFile(file, 'image');
      if (!validation.valid) {
        alert(validation.error || t('feed.file_invalid') || t('common.error'));
        return;
      }
      setFileType('image');
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    } else if (file.type.startsWith('video/')) {
      const validation = validateMediaFile(file, 'video');
      if (!validation.valid) {
        alert(validation.error || t('feed.file_invalid') || t('common.error'));
        return;
      }
      setFileType('video');
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      alert(t('feed.file_format_unsupported'));
    }
  };

  const removeFile = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl('');
    setFileType(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCreatePost = async () => {
    if (!newPostText.trim() && !selectedFile) {
      alert(t('feed.add_content'));
      return;
    }

    console.log('[POST] Creating post...', { hasText: !!newPostText.trim(), hasFile: !!selectedFile, fileType });
    setUploading(true);
    let previewUrlToCleanup: string | null = null;
    
    try {
      let imageUrl: string | undefined;
      let videoUrl: string | undefined;

      // Upload media if selected (using Firebase SDK - no CORS issues)
      if (selectedFile) {
        console.log('[POST] Uploading media...', { fileType, fileName: selectedFile.name, fileSize: selectedFile.size });
        try {
          if (fileType === 'image') {
            imageUrl = await uploadImage(selectedFile, user.id);
            console.log('[POST] Image uploaded successfully:', imageUrl);
          } else if (fileType === 'video') {
            videoUrl = await uploadVideo(selectedFile, user.id);
            console.log('[POST] Video uploaded successfully:', videoUrl);
          }
        } catch (uploadError: any) {
          console.error('[POST] Media upload failed:', uploadError);
          const errorMsg = t('feed.upload_error', { message: uploadError.message || t('feed.upload_failed') });
          throw new Error(errorMsg);
        }
      }

      // Create post in Firestore
      console.log('[POST] Creating post in Firestore...', { userId: user.id, imageUrl, videoUrl });
      const postId = await createPost(user.id, user.name, user.avatar, user.level, {
        text: newPostText.trim(),
        imageUrl,
        videoUrl,
        mediaType: fileType || undefined,
        type: 'general',
        privacy: 'public'
      });
      console.log('[POST] Post created successfully:', postId);

      // Cleanup preview URL if exists (from state)
      // Note: previewUrl is in state, cleanup is handled by removeFile()

      // Reset form
      setNewPostText('');
      removeFile();
      setShowCreate(false);
      alert(t('feed.post_success'));
      
      // Reload feed after creating post (since we're using one-shot instead of realtime)
      const reloadFeed = async () => {
        try {
          const feedPosts = await getPostsFeed(20);
          setPosts(feedPosts || []);
        } catch (error) {
          // Silently ignore reload errors
        }
      };
      reloadFeed();
    } catch (error: any) {
      console.error('[POST] Error creating post:', error);
      console.error('[POST] Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Cleanup preview on error
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        previewUrlToCleanup = previewUrl;
      }
      
      // Show user-friendly error message
      let errorMessage = t('feed.create_error');
      if (error.code === 'storage/unauthorized') {
        errorMessage = t('feed.storage_unauthorized');
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = t('feed.storage_quota_exceeded');
      } else if (error.message) {
        errorMessage = error.message;
      }
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return t('time.just_now');
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return t('time.just_now');
      if (diffMins < 60) return t('time.minutes_ago', { n: diffMins });
      if (diffHours < 24) return t('time.hours_ago', { n: diffHours });
      if (diffDays < 7) return t('time.days_ago', { n: diffDays });
      return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    } catch {
      return t('time.just_now');
    }
  };

  // Subscribe to following list for real-time suggestions updates and empty state intelligence
  useEffect(() => {
    // Load suggestions function
    const loadSuggestions = async (excludeIds: string[]) => {
      try {
        const excludeSet = new Set([user.id, ...excludeIds]);
        
        // Get all public profiles
        const { collection, getDocs } = await import('firebase/firestore');
        const { db } = await import('./firebaseConfig');
        const publicProfilesRef = collection(db, 'publicProfiles');
        const snapshot = await getDocs(publicProfilesRef);
        
        const suggestedProfiles = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as PublicProfile))
          .filter(profile => {
            // Exclude current user and already followed users
            if (excludeSet.has(profile.userId)) {
              return false;
            }
            // Only include public profiles
            if (profile.isPublic !== true) {
              return false;
            }
            return true;
          })
          .slice(0, 10);
        
        // Convert PublicProfile[] to PublicUserInfo[] for UI
        const suggestedUsers: PublicUserInfo[] = suggestedProfiles.map(profile => ({
          id: profile.userId,
          userId: profile.userId,
          name: profile.name,
          avatar: profile.avatar,
          level: profile.level,
          xp: profile.xp,
          title: profile.title,
        }));
        
        setSuggestions(suggestedUsers);
        
        // Pre-check follow status for suggestions
        const statusMap: Record<string, boolean> = {};
        await Promise.all(
          suggestedUsers.map(async (result) => {
            try {
              const following = await isFollowing(user.id, result.id);
              statusMap[result.id] = following;
            } catch (error) {
              statusMap[result.id] = false;
            }
          })
        );
        setFollowingStatus(prev => ({ ...prev, ...statusMap }));
      } catch (error: any) {
        if (import.meta.env.DEV) {
          console.error('[FEED] Error loading suggestions:', error);
        }
      }
    };
    
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    if (!authReady) return;
    
    // AUTH GUARD: Only subscribe if auth is valid
    if (!isValidAuth(user.id)) {
      if (import.meta.env.DEV) {
        console.warn('[FEED] Auth guard blocked following subscription - invalid auth');
      }
      setFollowingCount(0);
      return;
    }

    // Subscribe to real-time following changes
    let unsubscribeFollowing: (() => void) | null = null;
    try {
      unsubscribeFollowing = subscribeToFollowing(user.id, (ids) => {
        try {
          // Update following count for empty state intelligence
          setFollowingCount(ids.length || 0);
          // Reload suggestions when following list changes
          loadSuggestions(ids);
        } catch (callbackError: any) {
          // Callback errors must not crash
          if (import.meta.env.DEV) {
            console.error('[FEED] Error in following callback:', callbackError);
          }
          setFollowingCount(0);
        }
      });
    } catch (initError: any) {
      // Init errors must not crash
      if (import.meta.env.DEV) {
        console.error('[FEED] Error initializing following subscription:', initError);
      }
      setFollowingCount(0);
    }

    // Initial load
    loadSuggestions([]);

    return () => {
      if (typeof unsubscribeFollowing === 'function') {
        try {
          unsubscribeFollowing();
        } catch (cleanupError: any) {
          // Cleanup errors must not crash
          if (import.meta.env.DEV) {
            console.warn('[FEED] Error during following cleanup:', cleanupError);
          }
        }
      }
    };
  }, [authReady, user.id]);

  const searchUsers = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    if (import.meta.env.DEV) {
      console.log('[SEARCH] Starting user search for:', searchTerm);
    }
    
    try {
      // CRITICAL: Use searchPublicProfiles instead of reading from users collection
      const profiles = await searchPublicProfiles(searchTerm, user.id);
      
      if (import.meta.env.DEV) {
        console.log('[SEARCH] Found', profiles.length, 'public profiles matching:', searchTerm);
      }
      
      // Convert PublicProfile[] to PublicUserInfo[] for UI
      const results: PublicUserInfo[] = profiles.map(profile => ({
        id: profile.userId,
        userId: profile.userId,
        name: profile.name,
        avatar: profile.avatar,
        level: profile.level,
        xp: profile.xp,
        title: profile.title,
      }));
      
      if (import.meta.env.DEV) {
        console.log('[SEARCH] ✅ Search complete:', {
          searchTerm,
          resultsFound: results.length,
          currentUserId: user.id
        });
      }
      
      setSearchResults(results);
      
      // Check follow status for search results
      const statusMap: Record<string, boolean> = {};
      await Promise.all(
        results.map(async (result) => {
          try {
            const following = await isFollowing(user.id, result.id);
            statusMap[result.id] = following;
          } catch (error) {
            statusMap[result.id] = false;
          }
        })
      );
      setFollowingStatus(prev => ({ ...prev, ...statusMap }));
    } catch (error: any) {
      // CRITICAL: Handle errors gracefully - don't crash the app
      if (import.meta.env.DEV) {
        console.error('[SEARCH] ❌ Error searching users:', {
          code: error?.code,
          message: error?.message,
          searchTerm
        });
      }
      // On error, set empty results but don't block rendering
      setSearchResults([]);
    }
  };

  const handleFollow = async (targetId: string) => {
    try {
      await followUser(user.id, targetId);
      // Following status will update automatically via subscribeToFollowing
      setFollowingStatus(prev => ({ ...prev, [targetId]: true }));
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error following user:", error);
      }
    }
  };

  const handleUnfollow = async (targetId: string) => {
    try {
      await unfollowUser(user.id, targetId);
      // Following status will update automatically via subscribeToFollowing
      setFollowingStatus(prev => ({ ...prev, [targetId]: false }));
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error("Error unfollowing user:", error);
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchUsers(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-8 pb-32">
      {/* Feed Header */}
      <div className="flex items-center justify-between px-2">
        <h1 className="text-2xl font-black flex items-center gap-3">
          <TrendingUp className="text-emerald-500" /> {t('nav.feed')}
        </h1>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setShowSearch(!showSearch)}
            className="p-3 bg-slate-800 rounded-2xl text-slate-400 hover:text-emerald-400 hover:bg-slate-700 transition-all"
          >
            <Search size={24} />
          </button>
          <button 
            onClick={() => setShowCreate(true)}
            className="p-3 bg-emerald-500 rounded-2xl text-white shadow-lg shadow-emerald-500/20 active:scale-95 transition-all"
          >
            <PlusCircle size={24} />
          </button>
        </div>
      </div>

      {/* Search Bar */}
      {showSearch && (
        <div className="bg-slate-900 border border-slate-800 rounded-[2rem] p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Search className="text-slate-500" size={20} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('feed.search_users') || "Cerca utenti..."}
              className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2 text-white placeholder-slate-500 outline-none focus:border-emerald-500"
            />
            <button 
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
                setSearchResults([]);
              }}
              className="p-2 text-slate-500 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
          
          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {searchResults.map((resultUser) => (
                <div key={resultUser.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors">
                  <div className="flex items-center gap-3 cursor-pointer" onClick={() => setView && setView(`profile:${resultUser.id}`)}>
                    <img src={resultUser.avatar} alt={resultUser.name} className="w-10 h-10 rounded-full border border-slate-700" />
                    <div>
                      <p className="font-bold text-sm text-white">{resultUser.name}</p>
                      <p className="text-[10px] text-slate-500 uppercase">Level {resultUser.level}</p>
                    </div>
                  </div>
                  {followingStatus[resultUser.id] ? (
                    <button
                      onClick={() => handleUnfollow(resultUser.id)}
                      className="px-4 py-2 bg-slate-700 text-slate-400 rounded-xl text-xs font-black uppercase hover:bg-red-500/20 hover:text-red-400 transition-all"
                    >
                      {t('feed.unfollow') || 'Unfollow'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFollow(resultUser.id)}
                      className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-600 transition-all"
                    >
                      {t('feed.follow') || 'Follow'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* User Suggestions (when not searching) */}
          {!searchQuery && suggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest px-2">{t('feed.suggestions') || 'Suggestions'}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {suggestions.map((suggestedUser) => (
                  <div key={suggestedUser.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-xl hover:bg-slate-700 transition-colors">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={() => setView && setView(`profile:${suggestedUser.id}`)}>
                      <img src={suggestedUser.avatar} alt={suggestedUser.name} className="w-10 h-10 rounded-full border border-slate-700" />
                      <div>
                        <p className="font-bold text-sm text-white">{suggestedUser.name}</p>
                        <p className="text-[10px] text-slate-500 uppercase">Level {suggestedUser.level}</p>
                      </div>
                    </div>
                    {followingStatus[suggestedUser.id] ? (
                      <button
                        onClick={() => handleUnfollow(suggestedUser.id)}
                        className="px-4 py-2 bg-slate-700 text-slate-400 rounded-xl text-xs font-black uppercase hover:bg-red-500/20 hover:text-red-400 transition-all"
                      >
                        {t('feed.unfollow') || 'Unfollow'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleFollow(suggestedUser.id)}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-600 transition-all"
                      >
                        {t('feed.follow') || 'Follow'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Following Users List */}
      <FollowingUsersList user={user} authReady={authReady} setView={setView} />

      {/* Create Post Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-slate-900 w-full max-w-lg rounded-[2.5rem] border border-slate-800 p-8 space-y-6 animate-scaleIn max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-black flex items-center gap-2">
                <Sparkles className="text-emerald-400"/> {t('feed.new_post')}
              </h2>
              <button 
                onClick={() => {
                  removeFile();
                  setShowCreate(false);
                }} 
                className="p-2 bg-slate-800 rounded-xl text-white hover:bg-slate-700 transition-colors"
                disabled={uploading}
              >
                <X size={20} className="text-white" />
              </button>
            </div>

            <textarea 
              value={newPostText}
              onChange={e => setNewPostText(e.target.value)}
              placeholder={t('feed.post_placeholder')}
              className="w-full h-32 bg-slate-800 border border-slate-700 rounded-2xl p-4 outline-none focus:border-emerald-500 text-white resize-none"
              maxLength={500}
              disabled={uploading}
            />

            {/* File Preview */}
            {previewUrl && (
              <div className="relative">
                {fileType === 'image' && (
                  <img src={previewUrl} alt="Preview" className="w-full rounded-2xl object-cover max-h-64" />
                )}
                {fileType === 'video' && (
                  <video 
                    ref={videoRef}
                    src={previewUrl} 
                    controls 
                    className="w-full rounded-2xl object-cover max-h-64"
                  />
                )}
                <button
                  onClick={removeFile}
                  className="absolute top-2 right-2 p-2 bg-black/70 rounded-full text-white hover:bg-black/90 transition-all"
                  disabled={uploading}
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {/* File Input */}
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-3 bg-slate-800 rounded-xl text-slate-400 hover:text-emerald-400 hover:bg-slate-700 transition-all disabled:opacity-50"
                disabled={uploading}
              >
                {fileType === 'video' ? <Video size={20} /> : <ImageIcon size={20} />}
              </button>
              <span className="text-xs text-slate-500">
                {fileType === 'video' ? t('feed.video_selected') : fileType === 'image' ? t('feed.image_selected') : t('feed.add_media')}
              </span>
            </div>

            <button 
              onClick={handleCreatePost}
              disabled={uploading || (!newPostText.trim() && !selectedFile)}
              className="w-full bg-emerald-500 py-4 rounded-2xl font-black text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  {t('feed.uploading')}
                </>
              ) : (
                t('feed.post_button')
              )}
            </button>
          </div>
        </div>
      )}

      {/* Feed List */}
      <div className="space-y-8">
        {posts.length === 0 ? (
          <div className="text-center py-20 bg-slate-900/50 border border-slate-800 rounded-[2.5rem] p-8">
            {followingCount === 0 ? (
              // User follows no one - show CTA to follow people
              <>
                <div className="text-6xl mb-4">👥</div>
                <p className="text-slate-300 text-lg font-bold mb-2">Follow people to see their progress</p>
                <p className="text-slate-500 text-sm mb-6">Connect with others and build your community</p>
                <button
                  onClick={() => setShowSearch(true)}
                  className="px-6 py-3 bg-emerald-500 text-white rounded-xl font-black uppercase hover:bg-emerald-600 transition-all"
                >
                  Find People to Follow
                </button>
              </>
            ) : (
              // User follows people but feed is empty - different message
              <>
                <div className="text-6xl mb-4">🌅</div>
                <p className="text-slate-300 text-lg font-bold mb-2">Your network is quiet today</p>
                <p className="text-slate-500 text-sm mb-6">Be the one who moves first. Share your progress and inspire others.</p>
                <button
                  onClick={() => setShowCreate(true)}
                  className="px-6 py-3 bg-emerald-500 text-white rounded-xl font-black uppercase hover:bg-emerald-600 transition-all"
                >
                  Create Your First Post
                </button>
              </>
            )}
          </div>
        ) : (
          posts.map(post => (
            <div key={post.id} className="bg-slate-900 border border-slate-800 rounded-[2.5rem] overflow-hidden shadow-xl animate-fadeIn">
              {/* Post Header */}
              <div className="p-6 flex items-center justify-between">
                <div 
                  className="flex items-center gap-3 cursor-pointer"
                  onClick={() => setView && setView(`profile:${post.userId}`)}
                >
                  <img src={post.userAvatar} alt={post.userName} className="w-10 h-10 rounded-full border border-slate-800" />
                  <div>
                    <p className="font-bold text-sm">{post.userName}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black">
                      {formatTimestamp(post.timestamp)} • {post.type}
                    </p>
                  </div>
                </div>
                {post.userId === user.id ? (
                  <div className="relative group">
                    <button className="text-slate-500 hover:text-white"><MoreHorizontal size={20}/></button>
                    <div className="absolute right-0 top-8 bg-slate-900 border border-slate-800 rounded-xl shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto transition-all z-10 min-w-[120px]">
                      <button
                        onClick={async () => {
                          if (confirm(t('feed.delete_confirm'))) {
                            try {
                              const { deletePost } = await import('./services/postService');
                              await deletePost(post.id, user.id);
                              alert(t('feed.delete_success'));
                            } catch (error: any) {
                              console.error('[FEED] Error deleting post:', error);
                              alert(t('feed.delete_error') || t('common.error'));
                            }
                          }
                        }}
                        className="w-full text-left px-4 py-2 text-red-500 hover:bg-red-500/10 text-xs font-black uppercase tracking-widest transition-colors"
                      >
                        {t('feed.delete')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"><MoreHorizontal size={20}/></button>
                )}
              </div>

              {/* Post Text */}
              {post.text && (
                <div className="px-6 pb-4">
                  <p className="text-sm font-medium leading-relaxed text-slate-200">{post.text}</p>
                </div>
              )}

              {/* Post Media */}
              {post.imageUrl && (
                <div className="aspect-video bg-slate-800 overflow-hidden">
                  <img src={post.imageUrl} alt={t('feed.post_content_alt')} className="w-full h-full object-cover" />
                </div>
              )}
              
              {post.videoUrl && (
                <div className="aspect-video bg-slate-800 overflow-hidden">
                  <video 
                    src={post.videoUrl} 
                    controls 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {/* Post Actions */}
              <div className="p-4 px-6 border-t border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <button 
                    onClick={async () => {
                      try {
                        await likePost(post.id);
                      } catch (error: any) {
                        console.error('[FEED] Error liking post:', error);
                        alert(t('feed.like_error') || t('common.error'));
                      }
                    }} 
                    className="flex items-center gap-2 group transition-all"
                  >
                    <Heart className="group-hover:text-red-500 group-hover:scale-125 transition-all" size={20} />
                    <span className="text-xs font-black text-slate-500">{post.likes || 0}</span>
                  </button>
                  <button 
                    onClick={() => {
                      alert(t('feed.comments_coming_soon') || t('common.coming_soon'));
                    }}
                    className="flex items-center gap-2 group transition-all"
                  >
                    <MessageSquare className="group-hover:text-emerald-500 group-hover:scale-125 transition-all" size={20} />
                    <span className="text-xs font-black text-slate-500">{post.commentsCount || 0}</span>
                  </button>
                </div>
                <button 
                  onClick={async () => {
                    try {
                      const postUrl = `${window.location.origin}${window.location.pathname}?post=${post.id}`;
                      if (navigator.share) {
                        await navigator.share({
                          title: `${post.userName}'s post`,
                          text: post.text || '',
                          url: postUrl
                        });
                      } else {
                        // Fallback: copy to clipboard
                        await navigator.clipboard.writeText(postUrl);
                        alert(t('feed.share_copied'));
                      }
                    } catch (error: any) {
                      // User cancelled share or error occurred
                      if (error.name !== 'AbortError') {
                        console.error('[FEED] Error sharing post:', error);
                      }
                    }
                  }}
                  className="text-slate-500 hover:text-white transition-colors"
                >
                  <Share2 size={20}/>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
